import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import {TablePagination, useTheme} from "@material-ui/core";
import SystemContext from "../../../SystemContext";

import {
  useAddScheduledTripClient,
  useUpdateScheduledTripClient,
  useDeleteScheduledTripClient,
} from "../../../services/scheduledTrips/";
import useScheduledTripClients, {buildKey} from "../../../services/scheduledTrips/useScheduledTripClients";

import ClientCompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import AirportLookup from "../../Airports/components/AirportsLookup";
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import ScheduledTripClientsValidator from "../validators/ScheduledTripClientsValidator";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ComboSelect, MaskedNumber, MaskedText, UldAllocationSelect } from '../../../controls';
import ContractSelector from '../../../controls/ContractSelector';


const ScheduledTripClients = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const props = {
    canEdit: true,
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    year: props.year,
    month: props.month,
    trip: props.scheduledTripUuid,
  });

  const tableRef = createRef({state: {lastEditingRow: false}});
  const scheduledTripUuid = props.scheduledTripUuid;
  const legs = props.legs;

  const handleAddScheduledTripClient = useAddScheduledTripClient();
  const handleUpdateScheduledTripClient = useUpdateScheduledTripClient();
  const handleDeleteScheduledTripClient = useDeleteScheduledTripClient();

  const extraKeys = [scheduledTripUuid];
  const queryClient = useQueryClient();
  const tripClients = useScheduledTripClients({tableRef, queryClient, extraKeys});

  const checkCascade = (newData, oldData, callBack, resolve, reject) => {
    if (!props.hasChildren) {
      callBack(false, newData, oldData);
    } else {
      SystemData.confirm(
        "Cascade Changes",
        `Do you wish to cascade the changes to the Overrides`,
        [
          {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}}, 
          {icon: "cancel", value: false, label: "No", options: {color:"primary"}}
        ],
        (response) => {
          callBack(response, newData, oldData);
        }
      );
    }
  }

  const getTripClientColumns = (props) => {
    const baseColumn = {
      cellStyle: {padding: "8px 2px"},
      headerStyle: {padding: "8px 2px"},
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const formatChars = {
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const additionalValues = {legs};
    const validatorColumn = ValidatorColumn(ScheduledTripClientsValidator, false, additionalValues);

    return [
      {...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(200),
        cellStyle: {padding: "8px 2px 8px 12px"},
        headerStyle: {padding: "8px 2px 8px 12px"},
        title: "Client Company",
        field: "companyName",
        initialEditValue: "",
        editable: props.canEdit ? "onAdd" : "never",
        editComponent: (props) => (
          <ClientCompanyUuidLookup
            companyType={"CUSTOMER"}
            value={props.value}
            includeFreesale={true}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const customer = returnObject.data
                const data = {
                  ...props.rowData,
                  companyName: customer.companyName,
                  companyUuid: customer.companyUuid,
                  companyCode: customer.companyCode,
                  branchCode: customer.branchCode
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70, undefined, undefined, {textAlign: "center"}, {textAlign: "center"}, {textAlign: "center"}),
        title: "Origin",
        field: "origin",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,undefined, undefined, {textAlign: "center"}, {textAlign: "center"}, {textAlign: "center"}),
        title: "Dest.",
        field: "destination",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, baseColumn.cellStyle, baseColumn.headerStyle, undefined, false),
        title: "Contract Ref.",
        field: "contractUuid",
        initialEditValue: "",
        sorting: false,
        editComponent: (props) => (
          <div style={{marginTop: -3}}>
            <ContractSelector
              id="contractUuid"
              companyUuid={props.rowData.companyUuid}
              fullWidth
              value={props.value || ""}
              onChange={props.onChange}
            />
          </div>
        ),
        render: (rowData) => (
          <ContractSelector
            id="contractUuid"
            companyUuid={rowData.companyUuid}
            fullWidth
            value={rowData.contractUuid || ""}
            displayOnly={true}
          />
        ),
      },      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Allotment Code",
        field: "allotment Code",
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        sorting: false,
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "ULD Allocation",
        field: "uldAllocation",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <UldAllocationSelect
            value={props.value}
            onChange={(record) => {
              props.onRowDataChange({...props.rowData, ...record});
            }}
          />
        ),
        render: (rowdata) => (
          <UldAllocationSelect            
            value={rowdata.uldAllocation}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(160,undefined, undefined, {paddingRight: 10}, {paddingRight: 10}, {paddingRight: 10}, ),
        title: "Allotment KG",
        field: "allotmentKg",
        type: "numeric",
        emptyValue: null,
        sorting: false,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (rowdata) => (
          <MaskedNumber
            displayType="text"
            value={rowdata.allotmentKg}
            fixedDecimalScale={true}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(200),
        cellStyle: {padding: "8px 2px 8px 12px"},
        headerStyle: {padding: "8px 2px 8px 12px"},
        title: "Origin Cargo Handler",
        field: "originCargoHandlerCompanyName",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <ClientCompanyUuidLookup
            companyType={"CGHA"}
            atAirport={props.rowData.origin}
            value={props.value}
            returnType={"object"}
            allowEmpty={true}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const customer = returnObject.data;
                const data = {
                  ...props.rowData,
                  originCargoHandlerCompanyName: customer.companyName,
                  originCargoHandlerUuid: customer.companyUuid,
                  originCargoHandlerCompanyCode: customer.companyCode,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(200),
        cellStyle: {padding: "8px 2px 8px 12px"},
        headerStyle: {padding: "8px 2px 8px 12px"},
        title: "Destination Cargo Handler",
        field: "destinationCargoHandlerCompanyName",
        sorting: false,
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <ClientCompanyUuidLookup
            companyType={"CGHA"}
            atAirport={props.rowData.destination}
            value={props.value}
            returnType={"object"}
            allowEmpty={true}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const customer = returnObject.data
                const data = {
                  ...props.rowData,
                  destinationCargoHandlerCompanyName: customer.companyName,
                  destinationCargoHandlerUuid: customer.companyUuid,
                  destinationCargoHandlerCompanyCode: customer.companyCode,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,6,undefined,undefined,undefined,undefined,false),
        title: "Delivered",
        field: "cargoDeliveredAs",
        editable: state.canEdit ? "always" : "never",
        sorting: false,
        render: (rowData) => (
          <ComboSelect
            id="cargoDeliveredAs"
            group="CARGO DELIVERED AS"
            subGroup=""
            fullWidth
            value={rowData.cargoDeliveredAs}
            displayOnly={true}
            onChange={props.onChange}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="cargoDeliveredAs"
            group="CARGO DELIVERED AS"
            subGroup=""
            fullWidth
            value={props.value || ""}
            onChange={props.onChange}
          />
        )
      },
    ];
  };

  const postErrorHandler = (error) => {
    const {err, status, key} = {
      key: null, 
      status: null, 
      err: null,
      ...error
    };
    if (status === "ERROR" && err && err.response && err.response.data && err.response.data.error && err.response.data.errorMessage) {
      SystemData.alert("Error", `${err.response.data.errorMessage}`);
    }
  }

  return (isAuthenticated &&
    <div style={{marginLeft: 50,}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 5,
          detailPanelType: "single",
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        title="Scheduled Trip Clients"
        columns={getTripClientColumns({canEdit: state.canEdit})}
        data={tripClients.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAddCancelled: rowData => {
            const {key} = buildKey({extraKeys});
            queryClient.invalidateQueries(key);
          },
          onRowUpdateCancelled: rowData => {
            const {key} = buildKey({extraKeys});
            queryClient.invalidateQueries(key);
          },
          onRowAdd: state.canEdit
            ? (newData) => new Promise((resolve, reject) => {
              checkCascade(newData, newData, (response, newData, oldData) => {
                handleAddScheduledTripClient.mutate({record: {...newData, scheduledTripUuid, cascade: response}, resolve, reject, extraKeys, postError: postErrorHandler});
              })})
            : undefined,
          onRowUpdate: (newData, oldData) => new Promise((resolve, reject) => {
              checkCascade(newData, oldData, (response, newData, oldData) => {
                handleUpdateScheduledTripClient.mutate({record: {...newData, cascade: response}, oldRecord: oldData, resolve, reject, extraKeys, postError: postErrorHandler});
            })}),
          onRowDelete: (oldData) => new Promise((resolve, reject) => {
            checkCascade(oldData, oldData, (response, newData, oldData) => {
              handleDeleteScheduledTripClient.mutate({oldRecord: {...oldData, cascade: response}, resolve, reject, extraKeys, postError: postErrorHandler});
            })}),
        }}
      />
    </div>
  );

}

export default ScheduledTripClients;
